import React, { useState, useRef, useEffect } from 'react';
import {
  FiX,
  FiCalendar,
  FiClock,
  FiAlertTriangle,
  FiMail,
  FiUpload,
  FiLoader,
  FiShield,
  FiChevronDown,
  FiSearch,
  FiMapPin,
  FiCreditCard,
} from 'react-icons/fi';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { toast } from '../../utils/toast';
import { parseMailContactsCsvFile } from '../../utils/mailCsv';
import {
  useGetProfileEvents,
  isEventActive,
  getEventLocation,
  type EventItem,
} from '../../api/events';

// ─── Backdrop ───
export const ModalBackdrop: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
  onClose,
  children,
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4"
    onClick={onClose}
  >
    {children}
  </motion.div>
);

// ─── Content Wrapper (Responsive: bottom sheet on mobile, centered modal on desktop) ───
interface ModalWrapperProps {
  onClose: () => void;
  children: React.ReactNode;
  maxWidthClass: string;
}

export const ModalContentWrapper: React.FC<ModalWrapperProps> = ({
  onClose,
  children,
  maxWidthClass,
}) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  React.useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  if (isMobile) {
    return (
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 220 }}
        drag="y"
        dragConstraints={{ top: 0 }}
        dragElastic={{ top: 0.05, bottom: 0.75 }}
        onDragEnd={(event, info) => {
          if (info.velocity.y > 150 || info.offset.y > 180) {
            onClose();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-[#0A0A0A] rounded-t-[28px] border-t border-[#212121] flex flex-col overflow-hidden shadow-2xl pb-12 max-h-[92vh] origin-bottom"
      >
        {/* Grab Handle */}
        <div className="w-full flex justify-center py-3 shrink-0 cursor-grab active:cursor-grabbing">
          <div className="w-12 h-1.5 rounded-full bg-white/20" />
        </div>
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
      className={`w-full ${maxWidthClass} rounded-2xl bg-black border border-[#212121] flex flex-col overflow-hidden shadow-2xl max-h-[85vh]`}
    >
      {children}
    </motion.div>
  );
};

// ─── Schedule Email Modal ───
interface ScheduleModalProps {
  onClose: () => void;
  onSchedule: (date: Date) => void;
}

export const ScheduleModal: React.FC<ScheduleModalProps> = ({ onClose, onSchedule }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  const handleSchedule = () => {
    if (date && time) {
      const scheduledDate = new Date(`${date}T${time}`);
      onSchedule(scheduledDate);
    }
  };

  const getTodayString = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatPreview = () => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}`);
    return d.toLocaleString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[480px]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#262626] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2F80ED]/10 border border-[#2F80ED]/20 flex items-center justify-center">
              <FiCalendar size={18} className="text-[#2F80ED]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Schedule Delivery</h3>
              <p className="text-xs text-[#737373] mt-0.5">Pick a date and time to send</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors text-[#737373] hover:text-white"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 text-white">
          {/* Date Field */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Date <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <FiCalendar
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2F80ED] pointer-events-none z-10"
              />
              <input
                type="date"
                required
                min={getTodayString()}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-[#0D0D0D] border border-[#262626] text-sm text-white focus:outline-none focus:border-[#2F80ED] transition-colors [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Time Field */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Time <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <FiClock
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2F80ED] pointer-events-none z-10"
              />
              <input
                type="time"
                required
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-[#0D0D0D] border border-[#262626] text-sm text-white focus:outline-none focus:border-[#2F80ED] transition-colors [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Preview */}
          {formatPreview() && (
            <div className="rounded-xl bg-[#2F80ED]/[0.06] border border-[#2F80ED]/20 p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold mb-1.5">
                Will be sent on
              </p>
              <p className="text-sm text-white font-medium">{formatPreview()}</p>
            </div>
          )}

          {/* Info box */}
          <div className="rounded-xl bg-white/[0.03] border border-[#262626] p-3.5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
              <FiClock size={14} className="text-[#A3A3A3]" />
            </div>
            <div className="text-xs text-[#A3A3A3] leading-relaxed">
              <p className="text-white font-medium mb-0.5">About scheduled emails</p>
              <p>
                Your email will be queued and sent automatically at the chosen time. You can cancel
                or reschedule from the broadcasts list.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-[#262626] shrink-0 bg-[#0E0E0E]">
          <button
            onClick={onClose}
            className="flex-1 px-5 py-2.5 rounded-full border border-[#262626] text-sm text-white hover:bg-white/5 hover:border-[#404040] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={!date || !time}
            className="flex-1 px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Schedule
          </button>
        </div>
      </ModalContentWrapper>
    </ModalBackdrop>
  );
};

// ─── Delete Confirmation Modal ───
interface DeleteModalProps {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteConfirmationModal: React.FC<DeleteModalProps> = ({
  title,
  message,
  onClose,
  onConfirm,
}) => (
  <ModalBackdrop onClose={onClose}>
    <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[400px]">
      <div className="p-6 flex flex-col items-center text-center">
        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
          <FiAlertTriangle size={24} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-[#737373] mb-6">{message}</p>
        <div className="flex items-center gap-3 w-full">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-full border border-[#262626] text-sm text-white hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 h-11 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </ModalContentWrapper>
  </ModalBackdrop>
);

// ─── More Options Dropdown ───
interface MoreOptionsProps {
  options: { label: string; icon: React.ElementType; danger?: boolean; onClick: () => void }[];
  onClose: () => void;
}

export const MoreOptionsDropdown: React.FC<MoreOptionsProps> = ({ options, onClose }) => (
  <>
    <div className="fixed inset-0 z-10" onClick={onClose} />
    <div className="absolute right-0 top-full mt-1 w-48 rounded-xl bg-[#171717] border border-[#262626] shadow-xl z-20 overflow-hidden">
      {options.map((opt, i) => {
        const Icon = opt.icon;
        return (
          <button
            key={i}
            onClick={() => {
              opt.onClick();
              onClose();
            }}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
              opt.danger ? 'text-red-400 hover:bg-white/5' : 'text-white hover:bg-white/5'
            }`}
          >
            <Icon size={15} />
            {opt.label}
          </button>
        );
      })}
    </div>
  </>
);

// ─── New List Modal ───
interface NewListModalProps {
  onClose: () => void;
  onCreate: (name: string, emails: string[], eventId?: number) => void;
}

export const NewListModal: React.FC<NewListModalProps> = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const userId = typeof window !== 'undefined' ? localStorage.getItem('user_id') : undefined;
  const { data: profileEventsData, isLoading: eventsLoading } = useGetProfileEvents(
    userId ?? undefined,
    1,
    100,
  );

  const allEvents = profileEventsData?.results ?? [];
  const activePublishedEvents = allEvents.filter(
    (ev) => isEventActive(ev) && ev.is_draft_or_published === 'published',
  );

  const searchLower = eventSearch.trim().toLowerCase();
  const filteredEvents = searchLower
    ? activePublishedEvents.filter(
        (ev) =>
          (ev.event_title ?? '').toLowerCase().includes(searchLower) ||
          (ev.venue ?? ev.location ?? '').toLowerCase().includes(searchLower),
      )
    : activePublishedEvents;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setEventDropdownOpen(false);
      }
    }
    if (eventDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [eventDropdownOpen]);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    void parseMailContactsCsvFile(file).then((result) => {
      if (result.errors.length > 0) {
        toast.error(result.errors[0]);
        return;
      }

      const importedEmails = result.contacts.map((contact) => contact.email);
      if (importedEmails.length === 0) {
        toast.error('No valid emails found in CSV.');
        return;
      }

      setEmails((prev) => {
        const currentList = prev
          .split(/[\n,;]/)
          .map((x) => x.trim())
          .filter(Boolean);

        const seen = new Set<string>();
        const combined: string[] = [];

        [...currentList, ...importedEmails].forEach((email) => {
          const normalized = email.toLowerCase();
          if (!seen.has(normalized)) {
            seen.add(normalized);
            combined.push(email);
          }
        });

        return combined.join(', ');
      });

      if (result.summary.skippedRows > 0) {
        toast.warn(
          `Imported ${result.summary.validRows} contacts, skipped ${result.summary.skippedRows} rows.`,
        );
      } else {
        toast.success(`Imported ${result.summary.validRows} contacts`);
      }
    });

    e.target.value = '';
  };

  const handleSelectEvent = (ev: EventItem | null) => {
    setSelectedEvent(ev);
    setEventDropdownOpen(false);
    setEventSearch('');
  };

  const parsedEmailList = emails
    .split(/[\n,;]/)
    .map((x) => x.trim())
    .filter(Boolean);
  const hasValidEmail = parsedEmailList.some((e) => EMAIL_VALID_RE.test(e));
  const canSave = !!name.trim() && hasValidEmail;

  const handleSave = () => {
    if (!canSave) return;
    onCreate(name.trim(), parsedEmailList, selectedEvent?.id);
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[600px]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#262626] shrink-0">
          <h3 className="text-xl font-semibold text-white">New List</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors text-[#737373] hover:text-white"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 text-white">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Label"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-12 px-4 rounded-xl bg-transparent border border-[#262626] text-sm text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors"
              autoFocus
            />
          </div>

          {/* Attach Event Dropdown */}
          <div ref={dropdownRef} className="relative">
            <label className="block text-sm font-medium text-white mb-2">
              Attach Event (optional)
            </label>
            <button
              type="button"
              onClick={() => setEventDropdownOpen((prev) => !prev)}
              className="w-full h-12 px-4 rounded-xl bg-transparent border border-[#262626] text-sm text-white text-left flex items-center justify-between focus:outline-none focus:border-[#333] transition-colors hover:border-[#333]"
            >
              <span className={selectedEvent ? 'text-white' : 'text-[#737373]'}>
                {selectedEvent
                  ? (selectedEvent.event_title ?? 'Untitled Event')
                  : 'Select an event...'}
              </span>
              <FiChevronDown
                size={16}
                className={`text-[#737373] transition-transform duration-200 ${
                  eventDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {eventDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-[#1a1a1a] border border-[#333] rounded-xl shadow-2xl overflow-hidden">
                {/* Search */}
                <div className="p-3 border-b border-[#262626]">
                  <div className="relative flex items-center">
                    <FiSearch
                      size={14}
                      className="absolute left-3 text-[#737373] pointer-events-none"
                    />
                    <input
                      type="text"
                      value={eventSearch}
                      onChange={(e) => setEventSearch(e.target.value)}
                      placeholder="Search events..."
                      className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-3 h-9 text-white text-xs placeholder:text-[#737373] focus:outline-none focus:border-[#555] transition-colors"
                      autoFocus
                    />
                  </div>
                </div>

                {/* List */}
                <div className="max-h-[220px] overflow-y-auto">
                  {/* None option */}
                  <button
                    type="button"
                    onClick={() => handleSelectEvent(null)}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${
                      !selectedEvent
                        ? 'bg-blue-500/10 text-blue-400'
                        : 'text-[#737373] hover:bg-white/[0.04] hover:text-white'
                    }`}
                  >
                    <span className="w-3" />
                    None
                  </button>

                  {eventsLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                    </div>
                  ) : filteredEvents.length === 0 ? (
                    <p className="text-[#737373] text-xs text-center py-6 px-4">
                      {eventSearch ? 'No matching events' : 'No active events'}
                    </p>
                  ) : (
                    filteredEvents.map((ev) => {
                      const isSelected = selectedEvent?.id === ev.id;
                      const date = formatDate(ev.start);
                      const location = getEventLocation(ev);

                      return (
                        <button
                          key={ev.id}
                          type="button"
                          onClick={() => handleSelectEvent(ev)}
                          className={`w-full px-4 py-2.5 text-left transition-colors ${
                            isSelected ? 'bg-blue-500/10' : 'hover:bg-white/[0.04]'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 border-2 transition-colors ${
                                isSelected ? 'bg-blue-500 border-blue-500' : 'border-[#555]'
                              }`}
                            />
                            <div className="min-w-0">
                              <p
                                className={`text-sm font-medium truncate ${
                                  isSelected ? 'text-blue-400' : 'text-white'
                                }`}
                              >
                                {ev.event_title ?? 'Untitled Event'}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 text-[11px] text-[#737373]">
                                {date && (
                                  <span className="flex items-center gap-1">
                                    <FiCalendar size={10} />
                                    {date}
                                  </span>
                                )}
                                {location && (
                                  <span className="flex items-center gap-1 truncate">
                                    <FiMapPin size={10} />
                                    <span className="truncate">{location}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Emails Field */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <label className="text-sm font-medium text-white">
                Emails <span className="text-red-400">*</span>
              </label>
              <button
                onClick={handleImportClick}
                className="flex items-center gap-1.5 h-9 sm:h-10 px-3 sm:px-4 rounded-full bg-[#171717] hover:bg-white/10 text-[10px] sm:text-xs font-semibold text-white border border-[#262626] transition-colors cursor-pointer self-start"
              >
                <FiUpload size={11} className="text-[#2F80ED]" />
                Import list (csv)
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleCsvImport}
                accept=".csv"
                className="hidden"
              />
            </div>
            <div className="mb-2 text-right">
              <a
                href="/mail_contacts_sample.csv"
                download
                className="text-[11px] text-[#737373] hover:text-white transition-colors"
              >
                Download sample CSV
              </a>
            </div>
            <textarea
              placeholder="name@example.com, separate with commas"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className="w-full h-28 sm:h-32 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-transparent border border-[#262626] text-xs sm:text-sm text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-[#262626] shrink-0 bg-[#0E0E0E]">
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 rounded-full bg-white text-black text-xs sm:text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-40 cursor-pointer"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-black">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M5 8L7 10L11 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Save List
          </button>
        </div>
      </ModalContentWrapper>
    </ModalBackdrop>
  );
};

// ─── Edit Contact Modal ───
interface EditContactModalProps {
  contact: { email: string; name?: string; firstName?: string; lastName?: string };
  onClose: () => void;
  onSave: (contact: { email: string; name?: string }) => void;
  isSaving?: boolean;
}

const EMAIL_VALID_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EditContactModal: React.FC<EditContactModalProps> = ({
  contact,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const [email, setEmail] = useState(contact.email);
  const initialName =
    contact.name ?? [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  const [name, setName] = useState(initialName);
  const [touched, setTouched] = useState(false);

  const emailError =
    touched && !email.trim()
      ? 'Email is required.'
      : touched && !EMAIL_VALID_RE.test(email.trim())
        ? 'Enter a valid email address.'
        : null;

  const handleSave = () => {
    setTouched(true);
    if (!email.trim() || !EMAIL_VALID_RE.test(email.trim())) return;
    onSave({
      email: email.trim(),
      name: name.trim() || undefined,
    });
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[400px]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-[72px] border-b border-[#262626] shrink-0">
          <h2 className="text-lg font-semibold">Edit Contact</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
            <FiX size={20} className="text-[#737373]" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="block text-xs text-[#737373] mb-1.5 font-medium">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Contact name"
              className="w-full h-11 px-3 rounded-xl bg-[#0D0D0D] border border-[#262626] text-sm text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-[#737373] mb-1.5 font-medium">
              Email Address <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <FiMail
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (touched) setTouched(false);
                }}
                onBlur={() => setTouched(true)}
                placeholder="Enter email address"
                className={`w-full h-11 pl-9 pr-3 rounded-xl bg-[#0D0D0D] border text-sm text-white placeholder-[#737373] focus:outline-none transition-colors ${
                  emailError
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-[#262626] focus:border-[#333]'
                }`}
                autoFocus
              />
            </div>
            {emailError && (
              <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                <FiAlertTriangle size={10} />
                {emailError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#262626] shrink-0">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-full text-sm text-[#A3A3A3] hover:text-white transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !!emailError}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-40"
          >
            {isSaving && <FiLoader size={14} className="animate-spin" />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </ModalContentWrapper>
    </ModalBackdrop>
  );
};

// ─── Add Emails Modal (bulk add) ───
interface AddEmailsModalProps {
  onClose: () => void;
  onAdd: (emails: string[]) => void;
  listName: string;
}

export const AddEmailsModal: React.FC<AddEmailsModalProps> = ({ onClose, onAdd, listName }) => {
  const [emails, setEmails] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    void parseMailContactsCsvFile(file).then((result) => {
      if (result.errors.length > 0) {
        toast.error(result.errors[0]);
        return;
      }

      const importedEmails = result.contacts.map((contact) => contact.email);
      if (importedEmails.length === 0) {
        toast.error('No valid emails found in CSV.');
        return;
      }

      setEmails((prev) => {
        const currentList = prev
          .split(/[\n,;]/)
          .map((x) => x.trim())
          .filter(Boolean);

        const seen = new Set<string>();
        const combined: string[] = [];

        [...currentList, ...importedEmails].forEach((email) => {
          const normalized = email.toLowerCase();
          if (!seen.has(normalized)) {
            seen.add(normalized);
            combined.push(email);
          }
        });

        return combined.join(', ');
      });

      if (result.summary.skippedRows > 0) {
        toast.warn(
          `Imported ${result.summary.validRows} contacts, skipped ${result.summary.skippedRows} rows.`,
        );
      } else {
        toast.success(`Imported ${result.summary.validRows} contacts`);
      }
    });

    e.target.value = '';
  };

  const handleAdd = () => {
    const list = emails
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    if (list.length > 0) onAdd(list);
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[600px]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#262626] shrink-0">
          <div>
            <h3 className="text-xl font-semibold text-white">New Contact</h3>
            <p className="text-xs text-[#737373] mt-0.5">{listName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors text-[#737373] hover:text-white"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 text-white">
          {/* Emails Field */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white">
                Emails <span className="text-red-400">*</span>
              </label>
              <button
                onClick={handleImportClick}
                className="flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#171717] hover:bg-white/10 text-xs font-semibold text-white border border-[#262626] transition-colors cursor-pointer"
              >
                <FiUpload size={12} className="text-[#2F80ED]" />
                Import list (csv)
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleCsvImport}
                accept=".csv"
                className="hidden"
              />
            </div>
            <div className="mb-2 text-right">
              <a
                href="/mail_contacts_sample.csv"
                download
                className="text-[11px] text-[#737373] hover:text-white transition-colors"
              >
                Download sample CSV
              </a>
            </div>
            <textarea
              placeholder="name@example.com, separate with commas"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className="w-full h-32 p-4 rounded-xl bg-transparent border border-[#262626] text-sm text-white placeholder-[#737373] focus:outline-none focus:border-[#333] transition-colors resize-none"
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-[#262626] shrink-0 bg-[#0E0E0E]">
          <button
            onClick={handleAdd}
            disabled={!emails.trim()}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-40 cursor-pointer"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-black">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M5 8L7 10L11 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add Emails
          </button>
        </div>
      </ModalContentWrapper>
    </ModalBackdrop>
  );
};

// ─── Verification Required Modal ───

/** App Store download link for the Musicade mobile app */
export const MUSICADE_APP_DOWNLOAD_URL = 'https://apps.apple.com/us/app/musicade-app/id6747211062';

interface VerificationRequiredModalProps {
  message: string;
  onClose: () => void;
}

export const VerificationRequiredModal: React.FC<VerificationRequiredModalProps> = ({
  message,
  onClose,
}) => {
  return (
    <ModalBackdrop onClose={onClose}>
      <ModalContentWrapper onClose={onClose} maxWidthClass="max-w-[440px]">
        <div className="p-6 flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-5 ring-1 ring-amber-500/20">
            <FiShield size={28} className="text-amber-400" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-white mb-2">Stripe setup required</h3>

          {/* Message */}
          <p className="text-sm text-[#A3A3A3] leading-relaxed mb-6 max-w-[320px]">{message}</p>

          {/* Instruction */}
          <div className="w-full bg-[#0D0D0D] border border-[#262626] rounded-2xl p-5 mb-6">
            <p className="text-sm text-white font-medium mb-1">What to do</p>
            <p className="text-xs text-[#737373] leading-relaxed">
              Complete your Stripe setup in your account settings. Once set up, you'll be able to
              send emails from here.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 w-full">
            <Link
              to="/profile/settings/payments"
              className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-white text-black text-sm font-semibold hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <FiCreditCard size={16} />
              Setup Stripe
            </Link>
            <button
              onClick={onClose}
              className="w-full h-11 rounded-full border border-[#262626] text-sm text-[#A3A3A3] hover:text-white hover:border-[#404040] transition-colors cursor-pointer"
            >
              I'll do this later
            </button>
          </div>
        </div>
      </ModalContentWrapper>
    </ModalBackdrop>
  );
};
